// super-admin.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, tap, catchError, of } from 'rxjs';
import { authBackendEndPointToken } from '../app.config';
import { Router } from '@angular/router';

export interface PendingOrg {
  id: string;
  organizationName: string;
  fhirResourceId: string;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  adminPhone: string;
  status: number;
  submittedAt: string;
}

export interface ArchivedOrg {
  id: string;
  organizationName: string;
  fhirResourceId: string;
  status: number;
  reviewedAt: string;
  reviewedBy: string;
  rawFhir: any;
}

export enum DeletionStrategy {
  SoftDelete = 'SoftDelete',
  HardDelete = 'HardDelete',
  Archive = 'Archive'
}

@Injectable({
  providedIn: 'root'
})
export class SuperAdminService {
  private http = inject(HttpClient);
  private apiUrl = inject(authBackendEndPointToken);
  private dataRefreshTrigger$ = new Subject<void>();
  private router = inject(Router);

  // 🔔 Observable for components to subscribe to
  public onDataRefreshNeeded = this.dataRefreshTrigger$.asObservable();

  private triggerDataRefresh(): void {
    console.log('🔔 Triggering data refresh event');
    ///this.dataRefreshTrigger$.next();
    
    //const currentUrl = this.router.url;
  //this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
  //  this.router.navigate([currentUrl]);
  //});
  }



  /**
   * Get all organizations (SuperAdmin only)
   * ✅ Correct endpoint: /api/organization
   */



  getApprovedOrganizations(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/api/organization/approved`);
  }

  /**
   * Get organization by ID
   * ✅ Correct endpoint: /api/organization/{id}
   */
  getOrganizationById(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/api/organization/${id}`);
  }

  /**
   * Get pending organization approvals (SuperAdmin only)
   * ✅ Correct endpoint: /api/organization/approvals/pending
   */
  getPendingApprovals(): Observable<PendingOrg[]> {
    return this.http.get<PendingOrg[]>(`${this.apiUrl}/api/organization/approvals/pending`);
  }

  /**
   * Get archived organizations (SuperAdmin only)
   * ✅ Correct endpoint: /api/organization/archived
   */
  getArchivedOrganizations(): Observable<ArchivedOrg[]> {
    return this.http.get<ArchivedOrg[]>(`${this.apiUrl}/api/organization/archived`);
  }

  /**
   * Approve organization (SuperAdmin only)
   * ✅ Correct endpoint: /api/organization/approvals/{id}/approve
   */
  approveOrganization(approvalId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/organization/approvals/${approvalId}/approve`, {}, { observe: 'response' }).pipe(
      tap((response) => {
        console.log('✅ Organization approved successfully', response);
        // Trigger refresh after a short delay to ensure backend processing is complete
        setTimeout(() => this.triggerDataRefresh(), 800);
      }),
      catchError(error => {
        console.error('❌ Approval failed:', error);
        throw error;
      })
    );
  }

  /**
   * Reject organization (SuperAdmin only)
   * ✅ Correct endpoint: /api/organization/approvals/{id}/reject
   */
  rejectOrganization(approvalId: string, reason: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/organization/approvals/${approvalId}/reject`, { reason }, { observe: 'response' }).pipe(
      tap((response) => {
        console.log('❌ Organization rejected successfully', response);
        // Trigger refresh after a short delay to ensure backend processing is complete
        setTimeout(() => this.triggerDataRefresh(), 800);
      }),
      catchError(error => {
        console.error('❌ Rejection failed:', error);
        throw error;
      })
    );
  }

  deactivateOrganization(orgId: string, reason?: string): Observable<any> {
    const body = {
      reason: reason && reason.trim() ? reason.trim() : null
    };

    console.log('📤 Deactivating organization:', orgId, body);

    return this.http.post(
      `${this.apiUrl}/api/organization/${orgId}/deactivate`,
      body,
      { observe: 'response' }
    ).pipe(
      tap((response) => {
        console.log('✅ Organization deactivated successfully', response);
        // Trigger refresh after a short delay to ensure backend processing is complete
        setTimeout(() => this.triggerDataRefresh(), 800);
      }),
      catchError(error => {
        console.error('❌ Deactivation failed:', error);
        throw error;
      })
    );
  }

  /**
   * Permanently delete organization (ONLY for organizations with NO data)
   * ✅ Correct endpoint: DELETE /api/organization/{id}
   * WARNING: This action is irreversible!
   * @param orgId - Organization FHIR ID
   */
  deleteOrganizationPermanently(orgId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/api/organization/${orgId}`, { observe: 'response' }).pipe(
      tap((response) => {
        console.log(`🗑️ Organization permanently deleted successfully`, response);
        // Trigger refresh after a short delay to ensure backend processing is complete
        setTimeout(() => this.triggerDataRefresh(), 800);
      }),
      catchError(error => {
        console.error('❌ Permanent deletion failed:', error);
        throw error;
      })
    );
  }

  /**
   * Reactivate a deactivated or archived organization
   * ✅ Correct endpoint: /api/organization/{id}/reactivate
   */
  reactivateOrganization(orgId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/organization/${orgId}/reactivate`, {}, { observe: 'response' }).pipe(
      tap((response) => {
        console.log('♻️ Organization reactivated successfully', response);
        // Trigger refresh after a short delay to ensure backend processing is complete
        setTimeout(() => this.triggerDataRefresh(), 800);
      }),
      catchError(error => {
        console.error('❌ Reactivation failed:', error);
        throw error;
      })
    );
  }

  /**
   * ❌ DEPRECATED: This method is WRONG and should be removed
   * Use deactivateOrganization() or deleteOrganizationPermanently() instead
   */
  deleteOrganization(
    orgId: string,
    strategy: DeletionStrategy,
    reason?: string
  ): Observable<any> {
    console.warn('❌ DEPRECATED: deleteOrganization() is wrong. Use deactivateOrganization() or deleteOrganizationPermanently()');
    return this.http.delete(`${this.apiUrl}/api/organizations/${orgId}`, {
      body: { strategy, reason },
      observe: 'response'
    }).pipe(
      tap((response) => {
        console.log(`🗑️ Organization ${strategy} completed successfully`, response);
        setTimeout(() => this.triggerDataRefresh(), 800);
      }),
      catchError(error => {
        console.error('❌ Deletion failed:', error);
        throw error;
      })
    );
  }
}
